import { registerPlugin, WebPlugin } from '@capacitor/core';

/**
 * Aufnahme gesprochener Kommentare.
 *
 * Auf dem Gerät steckt dahinter das eigene Swift-Plugin (`ios/App/App/AudioRecorderPlugin.swift`)
 * auf `AVAudioRecorder` — für Capacitor 8 gibt es kein aktiv gepflegtes Fremdplugin, siehe
 * `docs/plugins.md`.
 *
 * Im Browser springt der Rückfall unten ein, damit sich der Ablauf am Rechner entwickeln lässt.
 * Er ist **nicht** für den Betrieb gedacht: `MediaRecorder` liefert je nach Browser andere
 * Formate, und in iPadOS-15-WebViews arbeitet er unzuverlässig — genau deshalb gibt es das
 * native Plugin.
 */

export interface RecordingResult {
  /** Die Aufnahme, ohne `data:`-Vorspann. */
  base64: string;
  durationSeconds: number;
  /** Dateiendung passend zum gelieferten Format. */
  extension: string;
}

export interface AudioRecorderPlugin {
  checkPermission(): Promise<{ granted: boolean }>;
  requestPermission(): Promise<{ granted: boolean }>;
  start(): Promise<void>;
  stop(): Promise<RecordingResult>;
  cancel(): Promise<void>;
  /** Packt eine Aufnahme in CAF um (für den Classic-Server). Nur auf dem Gerät. */
  zuCaf(options: { base64: string; endung: string }): Promise<{ base64: string }>;
}

/** Entwicklungs-Rückfall im Browser. */
class AudioRecorderWeb extends WebPlugin implements AudioRecorderPlugin {
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private startedAt = 0;

  async checkPermission(): Promise<{ granted: boolean }> {
    if (!navigator.permissions) return { granted: false };
    try {
      const status = await navigator.permissions.query({
        name: 'microphone' as PermissionName,
      });
      return { granted: status.state === 'granted' };
    } catch {
      // Firefox kennt den Namen „microphone" nicht — dann eben fragen statt prüfen.
      return { granted: false };
    }
  }

  async requestPermission(): Promise<{ granted: boolean }> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      return { granted: true };
    } catch {
      return { granted: false };
    }
  }

  async start(): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.chunks = [];
    this.recorder = new MediaRecorder(stream);
    this.recorder.ondataavailable = (e) => e.data.size > 0 && this.chunks.push(e.data);
    this.recorder.start();
    this.startedAt = Date.now();
  }

  async stop(): Promise<RecordingResult> {
    const recorder = this.recorder;
    if (!recorder) throw new Error('Es läuft keine Aufnahme.');

    const blob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () => resolve(new Blob(this.chunks, { type: recorder.mimeType }));
      recorder.stop();
    });
    recorder.stream.getTracks().forEach((t) => t.stop());
    this.recorder = null;

    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
      reader.readAsDataURL(blob);
    });

    return {
      base64,
      durationSeconds: (Date.now() - this.startedAt) / 1000,
      extension: recorder.mimeType.includes('mp4') ? 'm4a' : 'webm',
    };
  }

  async cancel(): Promise<void> {
    if (!this.recorder) return;
    this.recorder.stream.getTracks().forEach((t) => t.stop());
    this.recorder.stop();
    this.recorder = null;
    this.chunks = [];
  }

  async zuCaf(): Promise<{ base64: string }> {
    throw this.unavailable('CAF-Umwandlung gibt es nur auf dem Gerät.');
  }
}

export const AudioRecorder = registerPlugin<AudioRecorderPlugin>('AudioRecorder', {
  web: () => new AudioRecorderWeb(),
});
